import { useRef, useState } from 'react'
import { Command } from 'cmdk'
import { Check, ChevronDown, Search } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'

export interface SearchSelectOption {
  value: string
  label: string
  /** A short second line, also searched. */
  description?: string | null
}

const ALL = '__search_select_all__'

function matches(text: string, search: string): boolean {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean)
  const haystack = text.toLowerCase()
  return words.every((word) => haystack.includes(word))
}

/**
 * A dropdown for a long list of choices: type to narrow the list, scroll the rest, pick with the mouse
 * or the arrow keys and Enter. `allLabel` adds a first choice that clears the value (filters). On a phone
 * the list opens as a bottom sheet and the keyboard waits until Search is tapped (USA-1527).
 */
export function SearchSelect({
  value,
  options,
  onChange,
  allLabel,
  placeholder = 'Choose',
  searchPlaceholder = 'Search',
  emptyText = 'No matches',
  ariaLabel,
  title,
  id,
  disabled = false,
  className,
}: {
  value: string | undefined
  options: SearchSelectOption[]
  onChange: (value: string | undefined) => void
  allLabel?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  ariaLabel?: string
  /** The phone sheet's heading; defaults to the aria label or placeholder. */
  title?: string
  /** For a `<Label htmlFor>`. */
  id?: string
  disabled?: boolean
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const sheet = useMediaQuery(PHONE_QUERY)
  const sheetRef = useRef<HTMLDivElement>(null)
  const selected = options.find((option) => option.value === value)
  const shown = selected?.label ?? allLabel ?? placeholder

  const choose = (next: string | undefined) => {
    setOpen(false)
    setSearch('')
    onChange(next)
  }

  const trigger = (
    <button
      type="button"
      id={id}
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel}
      className={cn(
        'flex h-8 w-fit items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50',
        !selected && !allLabel && 'text-muted-foreground',
        className,
      )}
    >
      <span className="truncate">{shown}</span>
      <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden="true" />
    </button>
  )
  const list = (
    <Command
      className={cn(sheet && 'flex min-h-0 flex-1 flex-col')}
      filter={(itemValue, text, keywords) =>
        itemValue === ALL || matches((keywords ?? []).join(' '), text) ? 1 : 0
      }
    >
      <div className={cn('flex items-center gap-2 border-b px-3', sheet && 'border-t px-4')}>
        <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder={searchPlaceholder}
          className={cn(
            'h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground',
            sheet && 'h-11 text-base',
          )}
        />
      </div>
      <Command.List
        className={cn(
          'max-h-72 overflow-y-auto overscroll-contain p-1',
          sheet && 'max-h-none min-h-0 flex-1 px-2 pb-3 pt-1 [&_[cmdk-item]]:min-h-11 [&_[cmdk-item]]:py-2.5',
        )}
      >
        <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</Command.Empty>
        {allLabel && !search ? (
          <SearchSelectItem value={ALL} label={allLabel} selected={value == null} onSelect={() => choose(undefined)} />
        ) : null}
        {options.map((option) => (
          <SearchSelectItem
            key={option.value}
            value={option.value}
            keywords={[option.label, option.description ?? '']}
            label={option.label}
            description={option.description}
            selected={option.value === value}
            onSelect={() => choose(option.value)}
          />
        ))}
      </Command.List>
    </Command>
  )

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setSearch('')
  }

  if (sheet) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetTrigger asChild disabled={disabled}>
          {trigger}
        </SheetTrigger>
        <SheetContent
          ref={sheetRef}
          side="bottom"
          aria-describedby={undefined}
          // Focus the sheet, not the search field: the keyboard opens only when Search is tapped.
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            sheetRef.current?.focus()
          }}
          className="flex max-h-[85dvh] flex-col gap-0 rounded-t-xl p-0 pb-[env(safe-area-inset-bottom)] outline-none"
        >
          <SheetTitle className="px-4 pb-3 pt-4 pr-12 text-base font-semibold">
            {title ?? ariaLabel ?? placeholder}
          </SheetTitle>
          {list}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {trigger}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-(--radix-popover-trigger-width) min-w-72 p-0">
        {list}
      </PopoverContent>
    </Popover>
  )
}

function SearchSelectItem({
  value,
  keywords,
  label,
  description,
  selected,
  onSelect,
}: {
  value: string
  keywords?: string[]
  label: string
  description?: string | null
  selected: boolean
  onSelect: () => void
}) {
  return (
    <Command.Item
      value={value}
      keywords={keywords}
      onSelect={onSelect}
      className="flex cursor-default select-none items-start gap-2 rounded-sm px-2 py-1.5 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
    >
      <Check className={cn('mt-0.5 size-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
      <span className="min-w-0">
        <span className="block">{label}</span>
        {description ? <span className="block text-xs text-muted-foreground">{description}</span> : null}
      </span>
    </Command.Item>
  )
}
